import { Logger } from './logger';
import transform from 'lodash/transform';
import moment from 'moment';
import each from 'lodash/each';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';
import isNull from 'lodash/isNull';
import groupBy from 'lodash/groupBy';
import findIndex from 'lodash/findIndex';
import immutableSet from 'lodash/fp/set';

import { fromLocaleNumber } from './il8n/number-il8n';
import { normaliseToRegexSafeString } from './text/string-utils';

const logger = Logger.getLogger('sort-utils');

/**
 * Sorting, Dropdown, and filtering table data
 */

/**
 * @see https://imgur.com/gallery/v23nUwQ for description of SQL JOINS
 */
export const DROPDOWN_TYPES = {
  // dropdown mapped to multi columns, and all there combinations
  MULTI_COLUMN_EXCLUSIVE_FULL_JOIN: 'MULTI_COLUMN_EXCLUSIVE_FULL_JOIN',
  MULTI_COLUMN_INCLUSIVE_FULL_JOIN: 'MULTI_COLUMN_INCLUSIVE_FULL_JOIN',
  // DEFAULT: dropdown mapped to single data field
  STANDARD: 'STANDARD',
};

export const SORT_DIRECTIONS = {
  DESC: 'DESC',
  ASC: 'ASC',
};

/**
 * @see createCompare for alphabetical string, date or numerical sorting
 */
export function createSortByListCompare(orderedListOfThings) {
  return (thingA, thingB) => {
    const indexVehicleA = orderedListOfThings.indexOf(thingA);
    const indexVehicleB = orderedListOfThings.indexOf(thingB);
    if (indexVehicleA !== -1 && indexVehicleB !== -1) {
      return indexVehicleA > indexVehicleB ? 1 : -1;
    }
    if (indexVehicleA !== -1) {
      return -1;
    }
    if (indexVehicleB !== -1) {
      return 1;
    }
    return thingA > thingB ? 1 : -1;
  };
}

/**
 * WARNING: numerical sorting !== string sorting !!!
 * creates a sorting function, for use in [].sort(xxx)
 * @returns function for using in .sort
 * @see createSortByListCompare for matching array to arbitrary sort order from a list
 */
export function createCompare(options): any {
  const directionInt = options.direction === 'DESC' ? -1 : 1;
  if (options.isNumeric) {
    return _createNumericCompare(
      options.dataKey,
      directionInt,
      options.secondaryCompare
    );
  }
  if (options.isDate) {
    if (options.format === 'YYYY-MM-DD') {
      logger.warn(
        'Date Sorting YYYY-MM-DD Format, x1000 faster to do string sorting, doing string compare'
      );
      return _createStringCompare(
        options.dataKey,
        directionInt,
        options.secondaryCompare
      );
    }
    // Default date formatting to locale e.g. DD/MM/YYYY
    if (!options.dateFormat) {
      options.dateFormat = moment.localeData().longDateFormat('L');
    }
    return _createDateCompare(
      options.dataKey,
      directionInt,
      options.dateFormat,
      options.secondaryCompare
    );
  }
  return _createStringCompare(
    options.dataKey,
    directionInt,
    options.secondaryCompare
  );
}

function _createNumericCompare(
  dataKey: string,
  directionInt,
  secondaryCompare
) {
  return (a, b) => {
    const aParsed = get(a, dataKey, '');
    const bParsed = get(b, dataKey, '');
    return numericValueCompare(
      aParsed,
      bParsed,
      null,
      null,
      directionInt === -1
    );
  };
}

export function numericValueCompare(a, b, nodeA, nodeB, isInverted) {
  const aParsed = fromLocaleNumber(a);
  const bParsed = fromLocaleNumber(b);
  if ((isNull(aParsed) && isNull(bParsed)) || aParsed === bParsed) {
    return 0;
  }
  if (isNull(aParsed)) {
    // non-numbers always last
    return 1;
  }
  if (isNull(bParsed)) {
    // non-numbers always last
    return -1;
  }
  if (isInverted) {
    return bParsed - aParsed;
  }
  return aParsed - bParsed;
}

function _createStringCompare(dataKey, directionInt, secondaryCompare) {
  const dashes = ['\u2014', '&#8212;', '-', '—', ''];
  return (a, b) => {
    const aVal = (get(a, dataKey) || '').toString();
    const bVal = (get(b, dataKey) || '').toString();
    // depending on direction, make sure dashes or empty always last / bottom
    if (dashes.includes(aVal)) {
      // dashes or empty always last
      return 1;
    }
    if (dashes.includes(bVal)) {
      // dashes or empty always last
      return -1;
    }
    // string compare
    const result = aVal.localeCompare(bVal) * directionInt; // NB: by default, localeCompare() is case-insensitive
    if (result === 0 && secondaryCompare) {
      return secondaryCompare(a, b);
    }
    return result;
  };
}

/**
 * @param directionInt else 'ASC'
 * @param format moment date format
 */
function _createDateCompare(
  dataKey: string,
  directionInt: number,
  format: string,
  secondaryCompare?
) {
  return (a, b) => {
    const aParsed = moment(get(a, dataKey), format).valueOf();
    const bParsed = moment(get(b, dataKey), format).valueOf();
    if ((isNaN(aParsed) && isNaN(bParsed)) || aParsed === bParsed) {
      return secondaryCompare ? secondaryCompare(a, b) : 0;
    }
    if (isNaN(aParsed)) {
      // non-numbers (non-dates) always last
      return 1;
    }
    if (isNaN(bParsed)) {
      // non-numbers (non-dates) always last
      return -1;
    }
    return (aParsed - bParsed) * directionInt;
  };
}

/**
 * reducer safe modification of sorting filters array, returns clone,
 * doesn't modify source object
 */
export function toggleSortFilters(sortColumns, filterName) {
  sortColumns = cloneDeep(sortColumns);
  const isMap = sortColumns.values && !sortColumns.hasOwnProperty('values');
  if (isMap) {
    // Map Version
    const newSortColumn = sortColumns.get(filterName);
    switch (newSortColumn.direction) {
      case 'ASC':
        newSortColumn.direction = 'DESC';
        break;
      case 'DESC':
        newSortColumn.direction = 'ASC';
        break;
      default:
        newSortColumn.direction = newSortColumn.isNumeric ? 'DESC' : 'ASC';
    }
    sortColumns.forEach((value, key) => {
      if (key !== filterName) {
        sortColumns.set(
          key,
          Object.defineProperty(value, 'direction', {
            value: null,
            configurable: true,
          })
        );
      }
    });
  } else {
    // Object Version
    const sortColumn = sortColumns[filterName];
    if (sortColumn) {
      switch (sortColumn.direction) {
        case 'ASC':
          sortColumn.direction = 'DESC';
          break;
        case 'DESC':
          sortColumn.direction = 'ASC';
          break;
        default:
          sortColumn.direction = sortColumn.isNumeric ? 'DESC' : 'ASC';
      }
    }

    for (const columnName in sortColumns) {
      if (sortColumns.hasOwnProperty(columnName) && columnName !== filterName) {
        sortColumns[columnName].direction = null;
      }
    }
  }

  return sortColumns;
}

function _createSecondaryCompare(secondarySortConfig) {
  if (!secondarySortConfig) {
    return null;
  }
  const secondaryCompare = createCompare(secondarySortConfig);
  secondaryCompare.secondaryCompare = _createSecondaryCompare(
    secondarySortConfig.secondary
  );
  return secondaryCompare;
}

/**
 * apply sorting filters array, to data list
 *
 * supports two levels of sorting, and dynamic data keys
 */
export function applySorting(dataRows, stateTable) {
  const isMap =
    stateTable.columns.values && !stateTable.columns.hasOwnProperty('values');
  const sortColumns: any = isMap
    ? Array.from(stateTable.columns.values())
    : Object.values(stateTable.columns);
  const primarySortColumn = sortColumns.find((col) => col.direction);
  logger.debug('primarySortColumn', primarySortColumn);
  if (!primarySortColumn) {
    return dataRows;
  }
  const secondaryCompare = _createSecondaryCompare(stateTable.secondary);
  const comparePrimarySortColumn = Object.assign({}, primarySortColumn);
  comparePrimarySortColumn.secondaryCompare = secondaryCompare;
  const sortFunction = createCompare(comparePrimarySortColumn);
  dataRows.sort(sortFunction);
  return dataRows;
}

/**
 * PRIVATE FUNCTION, exported only for unit testing!
 *
 * this function fixes query strin encoding issues caused by utils-link.xsl template
 * <xsl:template match="link/query-string//parameter" mode="query-string">
 * XSL converts % into %25 which causes issues in filters. COREWEB-4448
 */
export function _fixAndDecode(param) {
  if (param === null) {
    return param;
  }
  param = decodeURIComponent(param);
  return param;
}

// SORTING AND FILTERING DIFFERENT SIDES OF THE SAME THING
// FILTERING FUNCTIONS

function _reorderArr(sourceArr, nameOrderArr) {
  const workingCopy = [].concat(sourceArr);
  const orderedArr = [];
  nameOrderArr.forEach((dropdownId) => {
    const tableFilterIndex = findIndex(workingCopy, { name: dropdownId });
    if (tableFilterIndex === -1 || !dropdownId) {
      logger.warn(`Unable to find Dropdown Filter for : ${dropdownId}`);
      return;
    }
    orderedArr.push(workingCopy[tableFilterIndex]);
    delete workingCopy[tableFilterIndex];
  });
  workingCopy.forEach((tableFilter) => {
    orderedArr.push(tableFilter);
  });
  return orderedArr;
}

/**
 * @param [queryStringParams] get from state params
 * @param [filterWhiteList] white list of filters to enable
 * @param [hideNonWhiteListed=false] hidden instead of filter non white listed
 */
export function createDropdownFilterConfig(
  tableFilters,
  tableRows,
  queryStringParams = null,
  filterWhiteList = null,
  hideNonWhiteListed = false
) {
  if (filterWhiteList) {
    if (hideNonWhiteListed) {
      // if has white list & to hide, then visible = false for unneeded filters
      tableFilters.map((tableFilter) => {
        tableFilter.visible = filterWhiteList.includes(tableFilter.name);
      });
    } else {
      // if has white list filter unneeded filters
      tableFilters = tableFilters.filter((tableFilter) => {
        return filterWhiteList.includes(tableFilter.name);
      });
    }
    // Reorder to White List Order
    tableFilters = _reorderArr(tableFilters, filterWhiteList);
  }
  tableFilters = tableFilters.map((tableFilter) => {
    // if has query string, select chosen filters
    if (queryStringParams) {
      let urlParam = queryStringParams[tableFilter.name];
      if (urlParam) {
        urlParam = _fixAndDecode(urlParam);
        tableFilter.selected = { value: urlParam };
      }
    }
    // set default selected
    if (!tableFilter.selected) {
      tableFilter.selected = { value: 'ALL', label: 'All' };
    }
    if (!tableFilter.hasOwnProperty('visible')) {
      tableFilter.visible = true;
    }
    // if doesn't have a hard coded options list
    if (!tableFilter.options && tableFilter.dataKey) {
      // TODO: functions which return functions are hard to understand, simplify to be simple function, since no function reuse
      tableFilter.options = _getUniqOptions(
        tableFilter.dataKey,
        true
      )(tableRows);
    }
    // if doesn't have a hard coded options list, and is multi data key
    if (!tableFilter.options && tableFilter.dataKeys) {
      const isInclusive =
        tableFilter.dropdownType ===
        DROPDOWN_TYPES.MULTI_COLUMN_INCLUSIVE_FULL_JOIN;
      tableFilter.options = _getFullUnionUniqOptions(
        tableRows,
        tableFilter.dataKeys,
        true,
        isInclusive
      );
    }
    return tableFilter;
  });
  return tableFilters;
}

/**
 * PRIVATE FUNCTION, exported only for unit testing!
 *
 * this function creates a function that will return an array of unique values of a
 * property when applied to an Array of objects, see the tests for an example
 * after COREWEB-1176, sorting is now case-insensitive
 * after COREWEB-3272, now optional parameter to filter out empty values
 */
export function _getUniqOptions(property, filterEmpty = false) {
  return (funds) => {
    let uniqueValues = uniq(funds.map((fund) => fund[property]));
    if (filterEmpty) {
      uniqueValues = uniqueValues.filter((value) => {
        return value !== '' && value !== undefined && value !== '-';
      });
    }
    let uniqOptions = uniqueValues.map((value) => {
      return {
        value,
        label: value,
      };
    });
    uniqOptions = uniqOptions.sort(
      createCompare({
        dataKey: 'label',
        direction: 'ASC',
      })
    );
    return uniqOptions;
  };
}

/**
 * PRIVATE FUNCTION, exported only for unit testing!
 *
 * this function creates a function that will return an array of unique values of a
 * property when applied to an Array of objects, see the tests for an example
 */
export function _getFullUnionUniqOptions(
  funds,
  dataKeys,
  isFilterEmpty = false,
  isInclusive = false
) {
  // get unique intersection
  const uniqueValues = uniqBy(funds, (fund) => {
    const propertiesFund = [];
    each(dataKeys, (property) => {
      let value = fund[property];
      if (!value || value === '-') {
        value = null;
      }
      propertiesFund.push(value);
    });
    const uniqueKey = propertiesFund.join(' - ');
    return uniqueKey;
  });
  // work out properties for labels
  let uniqOptions = [];
  each(uniqueValues, (uniqueValue) => {
    const propertiesFund = {};
    let hasValues = false;
    each(dataKeys, (property) => {
      const value = uniqueValue[property];
      if (!value || value === '-') {
        return;
      }
      //
      hasValues = true;
      propertiesFund[property] = value;
      //
      const uniqOption = {
        sortLabel: property + value,
        label: value,
        value: {},
      };
      uniqOption.value[property] = value;
      uniqOptions.push(uniqOption);
    });
    if (hasValues && isInclusive) {
      const combinedLabel = Object.values(propertiesFund).join(' - ');
      uniqOptions.push({
        sortLabel: 'Z' + combinedLabel,
        value: propertiesFund,
        label: combinedLabel,
      });
    }
  });
  // clean up duplicate labels
  uniqOptions = uniqBy(uniqOptions, 'label');
  // sort labels
  uniqOptions = uniqOptions.sort(
    createCompare({
      dataKey: 'sortLabel',
      direction: 'ASC',
    })
  );
  return uniqOptions;
}

/**
 * filter out all rows in given
 */
export function applyDropdownFilterConfig(tableDropdownFilters, tableRows) {
  const filteredRows = tableRows.filter((tableRow) => {
    let isVisible = true;
    tableDropdownFilters.forEach((tableDropdownFilter) => {
      // break early if previous filter failed
      if (!isVisible) {
        return;
      }
      // only filter on dropdowns with a value (this could be an array of values)
      if (!tableDropdownFilter.selected.value) {
        return;
      }
      const tableDropdownFilterValueIsArray = Array.isArray(
        tableDropdownFilter.selected.value
      );
      // only filter on array dropdowns with more than one value (if array of values)
      if (
        tableDropdownFilterValueIsArray &&
        !tableDropdownFilter.selected.value.length
      ) {
        return;
      }
      // only filter if value of array not ALL or HIDE_RATINGS
      const normalisedFilterValue = tableDropdownFilter.selected.value
        .toString()
        .toUpperCase();
      if (
        normalisedFilterValue === 'ALL' ||
        normalisedFilterValue === 'HIDE_RATINGS'
      ) {
        return;
      }
      switch (tableDropdownFilter.dropdownType) {
        case DROPDOWN_TYPES.MULTI_COLUMN_EXCLUSIVE_FULL_JOIN:
        case DROPDOWN_TYPES.MULTI_COLUMN_INCLUSIVE_FULL_JOIN:
          // filter if row matches selected values (plural)
          each(tableDropdownFilter.selected.value, (desiredValue, dataKey) => {
            const tableRowValue2 = tableRow[dataKey];
            // if no desired value for that value, move onto next
            if (!desiredValue) {
              return;
            }
            if (desiredValue !== tableRowValue2) {
              isVisible = false;
            }
          });
          break;
        default:
          // DROPDOWN_TYPES.STANDARD
          // filter if row matches selected value
          const tableRowValue = tableRow[tableDropdownFilter.dataKey];
          // TODO: create DROPDOWN_TYPES.MULTI_VALUE
          if (tableDropdownFilterValueIsArray) {
            if (
              tableDropdownFilter.selected.value.indexOf(tableRowValue) === -1
            ) {
              isVisible = false;
            }
            return;
          }
          if (tableDropdownFilter.selected.value !== tableRowValue) {
            isVisible = false;
          }
      }
    });
    return isVisible;
  });
  return filteredRows;
}

/**
 * filter out all rows in given fields that match a search term
 */
export function applySearchStringFilterConfig(
  searchString,
  searchFields,
  tableRows
) {
  searchString = searchString.toLowerCase();
  // if no table rows, return empty array
  if (!tableRows) {
    return [];
  }
  // OPTIMISATION: if empty search string, return all rows, no filtering required
  if (!searchString) {
    return tableRows;
  }
  // COREWEB-4907 / COREWEB-5073 normalise non breaking spaces
  const cleanedEscapedSearchValue = normaliseToRegexSafeString(searchString);
  const searchValueRegExp = new RegExp(cleanedEscapedSearchValue, 'ig');
  const filteredRows = tableRows.filter((tableRow) =>
    searchFields.reduce((isMatchingString, searchDataKey) => {
      // this goes through each of the fund's properties
      // and quick fork return true if any of them contain the search string
      if (isMatchingString) {
        return true;
      }
      const fieldValue = get(tableRow, searchDataKey, null);
      if (!fieldValue) {
        return false;
      }
      isMatchingString = fieldValue.match(searchValueRegExp);
      return isMatchingString;
    }, false)
  );
  return filteredRows;
}

/**
 * reducer safe modification of sorting filters array, returns clone,
 * doesn't modify source object
 */
export function updateDropdownFilter(
  dropdownFilters,
  dropdownName,
  dropdownValue
) {
  const dropdownFilterId = dropdownFilters.findIndex(
    (dropdown) => dropdown.name === dropdownName
  );
  const dropdownFilter = dropdownFilters[dropdownFilterId];
  if (
    !dropdownFilter ||
    isEqual(dropdownFilter.selected.value, dropdownValue.value)
  ) {
    return dropdownFilters;
  }
  const newDropdownFilters = immutableSet(
    `[${dropdownFilterId}].selected`,
    dropdownValue,
    dropdownFilters
  );
  return newDropdownFilters;
}

/**
 * reducer safe modification of sorting filters array, returns clone,
 * doesn't modify source object
 */
export function hasDropdownFilterChanged(
  dropdownFilters,
  dropdownName,
  dropdownValue
) {
  const dropdownFilterId = dropdownFilters.findIndex(
    (d) => d.name === dropdownName
  );
  const dropdownFilter = dropdownFilters[dropdownFilterId];
  if (
    !dropdownFilter ||
    isEqual(dropdownFilter.selected.value, dropdownValue.value)
  ) {
    return false;
  }
  return true;
}

/**
 * // WORKAROUND : Additional speed up mechanism
 *
 * if aurelia sees a Map in a for-each view loop it tries to be clever and wastes a lot of time comparing objects,
 * if you make sure Map keys are unique then aurelia will just throw away the old dom
 * instead of trying to modifiy existing dom, much faster for completely different
 * lists (e.g. after sorting)
 *
 * AT CLASS LEVEL
 *   let __aureliaForEachDirtyCheckWorkAround = 0;
 *
 * IN groupByShareClassesByVehicle
 *   __aureliaForEachDirtyCheckWorkAround++;
 *   .map(key => [key + '_' + __aureliaForEachDirtyCheckWorkAround, groupedShareClasses[key]]));
 */
export const AURELIA_MAP_SALT_CONTAINER = {
  AURELIA_MAP_SALT: 0,
};

/**
 * For Unit test usage
 */
export function resetSortUtilsCache() {
  AURELIA_MAP_SALT_CONTAINER.AURELIA_MAP_SALT = 0;
}

/**
 * creates shareClassesByVehicle/tableHash on state table
 * regenerating share classes by vehicle will also cause any tables to redraw
 */
export function regenerateShareClassesByVehicle(
  stateTable,
  groupDataKey = 'investmentVehicle'
) {
  AURELIA_MAP_SALT_CONTAINER.AURELIA_MAP_SALT++;
  const shareClasses = stateTable.sortedTableRows;
  // COREWEB-5302 group vehicle order can be passed through
  const orderedListOfVehicles = stateTable.orderedVehicleList || [];
  let shareClassesByVehicle = null;
  // COREWEB-3294 don't group if config set to show single vehicle
  if (stateTable.showSingleVehicle) {
    shareClassesByVehicle = new Map([
      [
        'ALL_' + AURELIA_MAP_SALT_CONTAINER.AURELIA_MAP_SALT,
        {
          groupName: 'ALL',
          sortedTableRows: stateTable.sortedTableRows,
        },
      ],
    ]);
  } else {
    // WORKAROUND: moving from view, and got rid of @computedFrom('shareClasses') as were being called twice
    const groupedShareClasses = groupBy(shareClasses, (shareClass) =>
      get(shareClass, groupDataKey)
    );
    // this creates an object of funds grouped by investment vehicle

    const sortByInvestmentVehicle = createSortByListCompare(
      orderedListOfVehicles
    );
    shareClassesByVehicle = new Map(
      Object.keys(groupedShareClasses)
        .sort(sortByInvestmentVehicle)
        .map((key) => [
          key + '_' + AURELIA_MAP_SALT_CONTAINER.AURELIA_MAP_SALT,
          {
            groupName: key,
            sortedTableRows: groupedShareClasses[key],
          },
        ])
    ); // turn this into a Map, so easier to use with Aurelia
  }
  stateTable.shareClassesByVehicle = shareClassesByVehicle;
  stateTable.tableHash = AURELIA_MAP_SALT_CONTAINER.AURELIA_MAP_SALT;
  return stateTable;
}

/**
 * COREWEB-5382 PPSS - Investment Vehicles display order in Vehilce filter
 * COREWEB-5447 PPSS - Sorting issue for Non English Vehicle names
 *
 * convert standard english investmentVehicle list, into localed investmentVehicle
 *
 * create orderedVehicleList for regenerateShareClassesByVehicle
 * @param englishVehicleList investment vehicles non localed
 * @return localed investment vehicles localed
 */
export function convertToLocaleVehicleList(
  englishVehicleList: string[],
  fundList: any[]
): string[] {
  const vehicleListKV = transform(fundList, (result, value: any, key) => {
    result[value.investmentVehicleStd] = value.investmentVehicle;
  });
  const localedVehicleList = englishVehicleList.map((value, key) => {
    const localedValue = vehicleListKV[value];
    if (!localedValue) {
      return value;
    }
    return localedValue;
  });
  return localedVehicleList;
}

// STATE EDITORS

/**
 * get table config from state,
 * apply sorting filters array, then to data list
 * then set immutably in state
 */
export function immutableChangeSortOnStateTable(
  tablePath: string,
  sortColumnName: string,
  state: any
) {
  let stateTable: any = get(state, tablePath);
  const columns = stateTable.columns;
  const isValidColumn = !!columns.get(sortColumnName);
  if (isValidColumn) {
    // keep immutable shallow clone state table
    stateTable = Object.assign({}, stateTable);
    stateTable.columns = toggleSortFilters(columns, sortColumnName);
    // Choose Filtered Data over non Filtered Data, clone data (no filtering required)
    // added inToggleView to prevent changing tableRows displayed when sorting not filtered data
    let tableRows =
      stateTable.filteredTableRows && !stateTable.inToggleView
        ? stateTable.filteredTableRows
        : stateTable.tableRows;
    tableRows = [].concat(tableRows);
    stateTable.sortedTableRows = applySorting(tableRows, stateTable);
    const newState = immutableSet(tablePath, stateTable, state);
    return newState;
  }
  return state;
}

/**
 * get table config from state,
 * apply filtering then sorting to data list
 * then set immutably in state
 *
 * @param tablePath path in state to Table Object
 */
export function immutableChangeFilterOnStateTable(
  tablePath: string,
  dropdownName: string,
  dropdownValue: any,
  state: any
) {
  let stateTable: any = get(state, tablePath);
  const dropdownFilterId = stateTable.dropdowns.findIndex(
    (d) => d.name === dropdownName
  );
  const dropdownFilter = stateTable.dropdowns[dropdownFilterId];
  if (
    !dropdownFilter ||
    isEqual(dropdownFilter.selected.value, dropdownValue.value)
  ) {
    return state;
  }
  // keep immutable shallow clone state table
  stateTable = Object.assign({}, stateTable);
  // apply dropdown change
  stateTable.dropdowns = updateDropdownFilter(
    stateTable.dropdowns,
    dropdownName,
    dropdownValue
  );
  const intermediateState = immutableSet(tablePath, stateTable, state);
  // rebuild filtering
  const newState = immutableRebuildSortingFilteringStateTable(
    tablePath,
    intermediateState
  );
  return newState;
}

/**
 * get table config from state,
 * apply filtering then sorting to data list
 * then set immutably in state
 *
 * @param tablePath path in state to Table Object
 */
export function immutableChangeSearchStringOnStateTable(
  tablePath: string,
  searchString: string,
  state: any
) {
  let stateTable = get(state, tablePath);
  // keep immutable shallow clone state table
  stateTable = Object.assign({}, stateTable);
  // apply search string change
  stateTable.searchString = searchString;
  const intermediateState = immutableSet(tablePath, stateTable, state);
  // rebuild filtering
  const newState = immutableRebuildSortingFilteringStateTable(
    tablePath,
    intermediateState
  );
  return newState;
}

/**
 * get table config from state,
 * apply filtering then sorting to data list
 * then set immutably in state
 *
 * @param tablePath path in state to Table Object
 */
export function immutableRebuildSortingFilteringStateTable(
  tablePath: string,
  state: any
) {
  let stateTable = get(state, tablePath);
  // keep immutable shallow clone state table
  stateTable = Object.assign({}, stateTable);
  // rebuild filtering
  stateTable.filteredTableRows = stateTable.tableRows;
  if (stateTable.hasOwnProperty('dropdowns')) {
    stateTable.filteredTableRows = applyDropdownFilterConfig(
      stateTable.dropdowns,
      stateTable.filteredTableRows
    );
  }
  if (
    stateTable.hasOwnProperty('searchString') &&
    stateTable.hasOwnProperty('searchFields')
  ) {
    stateTable.filteredTableRows = applySearchStringFilterConfig(
      stateTable.searchString,
      stateTable.searchFields,
      stateTable.filteredTableRows
    );
  }
  // rebuild sorting
  stateTable.sortedTableRows = applySorting(
    stateTable.filteredTableRows,
    stateTable
  );
  const newState = immutableSet(tablePath, stateTable, state);
  return newState;
}

export function floatComparator(
  value1: string,
  value2: string,
  nodeA,
  nodeB,
  isInverted
): number {
  const aParsed = fromLocaleNumber(value1);
  const bParsed = fromLocaleNumber(value2);
  if ((isNull(aParsed) && isNull(bParsed)) || aParsed === bParsed) {
    return 0;
  }
  if (isNull(aParsed)) {
    // non-numbers always last
    return isInverted ? -1 : 1;
  }
  if (isNull(bParsed)) {
    // non-numbers always last
    return isInverted ? 1 : -1;
  }
  return aParsed - bParsed;
}

export function createDateComparator(format: string) {
  return (value1: string, value2: string, nodeA, nodeB, isInverted) => {
    const aParsed = moment(value1, format).valueOf();
    const bParsed = moment(value2, format).valueOf();
    if ((isNaN(aParsed) && isNaN(bParsed)) || aParsed === bParsed) {
      return 0;
    }
    if (isNaN(aParsed)) {
      // non-numbers always last
      return isInverted ? -1 : 1;
    }
    if (isNaN(bParsed)) {
      // non-numbers always last
      return isInverted ? 1 : -1;
    }
    return aParsed - bParsed;
  };
}

export function stringCompare(
  value1: string,
  value2: string,
  nodeA?: any,
  nodeB?: any,
  isInverted?: boolean
) {
  const dashes = ['\u2014', '&#8212;', '-', '—', ''];
  // depending on direction, make sure dashes or empty always last / bottom
  if (dashes.includes(value1)) {
    // dashes or empty always last
    return 1;
  }
  if (dashes.includes(value2)) {
    // dashes or empty always last
    return -1;
  }

  return value1.localeCompare(value2);
}

// Handled literature related  sorting
export const literatureSort = (
  valueA: string,
  valueB: string,
  nodeA?: any,
  nodeB?: any,
  isInverted?: boolean
): number => {
  // For handling mobile literature data
  const dataA = nodeA?.data || nodeA;
  const dataB = nodeB?.data || nodeB;

  // Sort by ticker on the basis of matchesOnTicker property.
  if (dataA?.matchesOnTicker > dataB?.matchesOnTicker) {
    return -1;
  }

  if (dataA?.matchesOnTicker < dataB?.matchesOnTicker) {
    return 1;
  }

  // string compare: if equal, sort alphabetically instead.
  const compareValue = stringCompare(valueA, valueB, nodeA, nodeB);

  // Added secondary level sorting on publication date(frkReferenceDate) for same document name.
  if (!compareValue) {
    const publicationDate1 = new Date(
      dataA?.literatureData?.frkReferenceDate
    ).getTime();
    const publicationDate2 = new Date(
      dataB?.literatureData?.frkReferenceDate
    ).getTime();
    return publicationDate2 - publicationDate1;
  } else {
    return compareValue;
  }
};

/* tyring to sort product name based on age*/
export function getNameForSorting(name: string): string {
  if (name.indexOf(' Newborn') !== -1) {
    return name.substring(0, name.indexOf('Newborn'));
  } else if (name.indexOf(' -') !== -1) {
    return name.substring(0, name.indexOf(' -'));
  } else if (name.indexOf('-') !== -1) {
    return name.substring(0, name.indexOf('-'));
  } else if (name.indexOf('+') !== -1) {
    return name.substring(0, name.indexOf('+'));
  } else if (name.indexOf(' Year') !== -1) {
    return name.substring(0, name.indexOf(' Year'));
  } else {
    return name;
  }
}
